import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";

import BaseConverter from "./converters/baseConverter";

import type { BaseApiResponse } from "../types/BaseApiResponse";
import { baseApiResponse } from "../types/BaseApiResponse";
import type { BootsrapModel, SendSupportRequestParamsModel } from "../types/BootsrapModel";
import { bootsrapResolver } from "../types/BootsrapModel";
import type { GetCountriesResult } from "../types/CountryModel";
import { getCountriesResult } from "../types/CountryModel";
import type { Currencies } from "../types/Currency";
import type { GetCurrenciesResult } from "../types/CurrencyModel";
import { getCurrenciesResult } from "../types/CurrencyModel";
import type { ListPaymentSystemsResult } from "../types/PaymentSystemModel";
import { listPaymentSystemsResult } from "../types/PaymentSystemModel";

const useSystemService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			getBootstrap: async (params: { refresh?: boolean; slim?: boolean }) => {
				return BaseConverter.convert<BootsrapModel>(
					await actions.get(API_URLS.BOOTSTRAP.GET, params, {
						resolvers: [
							{
								resolver: bootsrapResolver
							}
						]
					})
				);
			},
			sendRequestToSupport: async (params: SendSupportRequestParamsModel) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.post(API_URLS.SUPPORT.EMAIL, params, {
						resolvers: [
							{
								resolver: baseApiResponse
							}
						]
					})
				);
			},
			getCountryList: async (order?: "asc" | "desc") => {
				return BaseConverter.convert<GetCountriesResult>(
					await actions.get(
						API_URLS.COUNTRIES.LIST,
						{
							orderHow: order || "asc"
						},
						{
							resolvers: [
								{
									resolver: getCountriesResult
								}
							]
						}
					)
				);
			},
			getCurrencyList: async () => {
				return BaseConverter.convert<GetCurrenciesResult>(
					await actions.get(API_URLS.CURRENCIES.LIST, null, {
						resolvers: [
							{
								resolver: getCurrenciesResult
							}
						]
					})
				);
			},
			getPaymentSystems: async (params?: { version?: string; currency?: Currencies }) => {
				return BaseConverter.convert<ListPaymentSystemsResult>(
					await actions.get(API_URLS.PAYMENT.LIST_SYSTEMS, params, {
						resolvers: [
							{
								resolver: listPaymentSystemsResult
							}
						]
					})
				);
			}
		}),
		[actions]
	);
};

export default useSystemService;
